import {
  AppWindow,
  ArrowRight,
  BookOpen,
  Calculator,
  Globe2,
  Palette,
  Search,
  ServerCog,
  type LucideIcon,
} from "lucide-react";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

type EcosystemCard = {
  icon: LucideIcon;
  titleKey:
    | "navSolutionsCustomization"
    | "navSolutionsAlcatraz"
    | "navSolutionsIptvApp"
    | "navSolutionsLogistics";
  descriptionKey:
    | "navSolutionsCustomizationDesc"
    | "navSolutionsAlcatrazDesc"
    | "navSolutionsIptvAppDesc"
    | "navSolutionsLogisticsDesc";
  href: string;
  feature?: boolean;
};

const ecosystemCards: EcosystemCard[] = [
  {
    icon: Palette,
    titleKey: "navSolutionsCustomization",
    descriptionKey: "navSolutionsCustomizationDesc",
    href: "/solutions/customization",
    feature: true,
  },
  {
    icon: ServerCog,
    titleKey: "navSolutionsAlcatraz",
    descriptionKey: "navSolutionsAlcatrazDesc",
    href: "/solutions/alcatraz-dms",
  },
  {
    icon: AppWindow,
    titleKey: "navSolutionsIptvApp",
    descriptionKey: "navSolutionsIptvAppDesc",
    href: "/solutions/iptv-app",
  },
  {
    icon: Globe2,
    titleKey: "navSolutionsLogistics",
    descriptionKey: "navSolutionsLogisticsDesc",
    href: "/solutions/logistics",
  },
];

export function StbEcosystem() {
  const { messages, site, href } = useSite();
  const searchHref = localizeUrl("/search", site.locale, href);
  const resources = [
    {
      icon: BookOpen,
      title: messages.sbcBreadcrumbPage,
      href: localizeUrl("/guides/iptv-stb-buyers-checklist", site.locale, href),
    },
    {
      icon: Calculator,
      title: messages.tcoCardTitle,
      href: localizeUrl("/tools/stb-tco-calculator", site.locale, href),
    },
    {
      icon: ArrowRight,
      title: messages.guidesIndexAllTitle,
      href: localizeUrl("/guides", site.locale, href),
    },
  ];

  return (
    <section className="border-b border-border/60 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-wider text-primary">
            {messages.portalEyebrow}
          </p>
          <h2 className="mt-2 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
            {messages.portalTitle}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">{messages.portalDescription}</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {ecosystemCards.map((card) => {
            const Icon = card.icon;
            return (
              <a
                key={card.href}
                href={localizeUrl(card.href, site.locale, href)}
                className={`group relative flex min-h-56 flex-col justify-between overflow-hidden rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/35 hover:shadow-glow ${
                  card.feature
                    ? "border-primary/25 bg-primary/[0.06] sm:col-span-2 lg:col-span-2"
                    : "border-border bg-card"
                }`}
              >
                {card.feature && (
                  <div
                    aria-hidden
                    className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/15 blur-3xl"
                  />
                )}
                <div className="relative">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/10">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
                    {messages[card.titleKey]}
                  </h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                    {messages[card.descriptionKey]}
                  </p>
                </div>
                <span className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                  {messages.learnMore}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            );
          })}
        </div>

        <div className="relative mt-8 overflow-hidden rounded-3xl border border-white/10 bg-[#06162f] text-white shadow-[0_28px_80px_-44px_rgba(6,22,47,0.9)]">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(circle at 18% 10%, rgba(74,168,255,0.52), transparent 34%), radial-gradient(circle at 92% 90%, rgba(22,136,243,0.35), transparent 28%)",
            }}
          />
          <div className="relative grid gap-8 p-7 sm:p-9 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12 lg:p-12">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-300">
                {messages.portalResourcesEyebrow}
              </p>
              <h3 className="mt-3 max-w-xl text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.portalResourcesTitle}
              </h3>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/65 sm:text-base">
                {messages.portalResourcesDescription}
              </p>

              <form
                action={searchHref}
                method="get"
                role="search"
                autoComplete="off"
                className="relative mt-7 max-w-xl"
              >
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45" />
                <input
                  type="search"
                  name="q"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder={messages.searchPlaceholder}
                  aria-label={messages.navSearch}
                  className="h-12 w-full rounded-xl border border-white/15 bg-white/[0.08] pl-11 pr-28 text-sm text-white outline-none placeholder:text-white/40 focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/20"
                />
                <button
                  type="submit"
                  className="absolute bottom-1.5 right-1.5 top-1.5 rounded-lg bg-white px-4 text-xs font-semibold text-slate-950 transition-colors hover:bg-sky-100"
                >
                  {messages.navSearch}
                </button>
              </form>
            </div>

            <nav aria-label={messages.portalResourcesEyebrow} className="grid gap-2">
              {resources.map(({ icon: Icon, title, href: resourceHref }) => (
                <a
                  key={resourceHref}
                  href={resourceHref}
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3.5 transition-colors hover:border-sky-300/30 hover:bg-white/[0.1]"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-400/15 text-sky-300">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-white">{title}</span>
                  <ArrowRight className="ml-auto h-4 w-4 text-white/45 transition-transform group-hover:translate-x-0.5 group-hover:text-sky-300" />
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </section>
  );
}
